/**
 * Serviço de Usuários do Sistema MEDICARE
 * Gerencia todas as operações relacionadas a usuários
 */

import { ApiService, buildQueryString } from './api';
import {
  Usuario,
  CriarUsuarioData,
  AtualizarUsuarioData,
  LoginData,
  LoginResponse,
  FiltroUsuarios,
  PaginatedResponse,
  ApiResponse,
  UpdateProfileResponse,
  CriarUsuarioResponse,
} from '../types';

export class UsuariosService {
  private static readonly BASE_URL = '/admin/usuarios';

  /**
   * Realizar login do usuário
   */
  static async login(dados: LoginData): Promise<ApiResponse<LoginResponse>> {
    return ApiService.post<LoginResponse>('/auth/login', dados);
  }

  /**
   * Obter perfil do usuário logado
   */
  static async obterPerfil(): Promise<ApiResponse<Usuario>> {
    return ApiService.get<Usuario>('/usuarios/perfil');
  }

  /**
   * Alias para atualizarPerfil (compatibilidade)
   */
  static async updateProfile(dados: Partial<Usuario>): Promise<ApiResponse<UpdateProfileResponse>> {
    return UsuariosService.atualizarPerfil(dados);
  }

  /**
   * Listar usuários com filtros e paginação
   */
  static async listar(filtros: FiltroUsuarios = {}): Promise<ApiResponse<PaginatedResponse<Usuario>>> {
    const queryString = buildQueryString(filtros);
    const url = queryString ? `${this.BASE_URL}?${queryString}` : this.BASE_URL;
    return ApiService.get<PaginatedResponse<Usuario>>(url);
  }

  /**
   * Obter usuário por ID
   */
  static async obterPorId(id: string): Promise<ApiResponse<Usuario>> {
    return ApiService.get<Usuario>(`${this.BASE_URL}/${id}`);
  }

  /**
   * Criar novo usuário
   */
  static async criar(dados: CriarUsuarioData): Promise<ApiResponse<CriarUsuarioResponse>> {
    return ApiService.post<CriarUsuarioResponse>(`${this.BASE_URL}`, dados);
  }

  /**
   * Atualizar usuário
   */
  static async atualizar(id: string, dados: AtualizarUsuarioData): Promise<ApiResponse<Usuario>> {
    // Filtrar apenas os campos permitidos pelo backend
    const camposPermitidos = ['nome', 'email', 'tipo_usuario', 'ativo', 'dados_adicionais', 'endpoint_id', 'prompt_ia'];
    const dadosFormatados: any = {};
    
    // Copiar apenas campos permitidos
    camposPermitidos.forEach(campo => {
      if (campo in dados) {
        dadosFormatados[campo] = (dados as any)[campo];
      }
    });
    
    // Mapear 'tipo' para 'tipo_usuario' se presente
    if ('tipo' in dados) {
      dadosFormatados.tipo_usuario = (dados as any).tipo;
    }
    
    // Validar UUID do endpoint_id
    if (dadosFormatados.endpoint_id !== undefined && dadosFormatados.endpoint_id !== '') {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(dadosFormatados.endpoint_id)) {
        dadosFormatados.endpoint_id = undefined;
      }
    }
    
    return ApiService.put<Usuario>(`${this.BASE_URL}/${id}`, dadosFormatados);
  }

  /**
   * Desativar usuário (soft delete)
   */
  static async desativar(id: string): Promise<ApiResponse<void>> {
    return ApiService.delete<void>(`${this.BASE_URL}/${id}`);
  }

  /**
   * Alias para desativar (compatibilidade)
   */
  static async delete(id: string): Promise<ApiResponse<void>> {
    return this.desativar(id);
  }

  /**
   * Ativar usuário
   */
  static async ativar(id: string): Promise<ApiResponse<Usuario>> {
    return ApiService.put<Usuario>(`${this.BASE_URL}/${id}`, { ativo: true });
  }

  /**
   * Alterar senha do usuário logado
   */
  static async alterarSenha(senhaAtual: string, novaSenha: string): Promise<ApiResponse<void>> {
    return ApiService.put<void>(`${this.BASE_URL}/perfil/senha`, {
      senha_atual: senhaAtual,
      nova_senha: novaSenha,
    });
  }

  /**
   * Alias para alterarSenha (compatibilidade)
   */
  static async changePassword(dados: { senhaAtual: string; novaSenha: string }): Promise<ApiResponse<void>> {
    return this.alterarSenha(dados.senhaAtual, dados.novaSenha);
  }

  /**
   * Solicitar redefinição de senha
   */
  static async solicitarRedefinicaoSenha(email: string): Promise<ApiResponse<void>> {
    return ApiService.post<void>('/auth/esqueci-senha', { email });
  }

  /**
   * Redefinir senha com token
   */
  static async redefinirSenha(token: string, novaSenha: string): Promise<ApiResponse<void>> {
    return ApiService.post<void>('/auth/nova-senha', {
      token,
      novaSenha: novaSenha,
    });
  }

  /**
   * Verificar se email já existe
   */
  static async verificarEmail(email: string): Promise<ApiResponse<{ existe: boolean }>> {
    return ApiService.get<{ existe: boolean }>(`${this.BASE_URL}/verificar-email?email=${encodeURIComponent(email)}`);
  }

  /**
   * Verificar se CRM já existe
   */
  static async verificarCRM(crm: string): Promise<ApiResponse<{ existe: boolean }>> {
    return ApiService.get<{ existe: boolean }>(`${this.BASE_URL}/verificar-crm?crm=${encodeURIComponent(crm)}`);
  }

  /**
   * Verificar se COREN já existe
   */
  static async verificarCOREN(coren: string): Promise<ApiResponse<{ existe: boolean }>> {
    return ApiService.get<{ existe: boolean }>(`${this.BASE_URL}/verificar-coren?coren=${encodeURIComponent(coren)}`);
  }

  /**
   * Verificar se CPF já existe
   */
  static async verificarCPF(cpf: string): Promise<ApiResponse<{ existe: boolean }>> {
    return ApiService.get<{ existe: boolean }>(`${this.BASE_URL}/verificar-cpf?cpf=${encodeURIComponent(cpf)}`);
  }

  /**
   * Obter estatísticas de usuários
   */
  static async obterEstatisticas(): Promise<ApiResponse<{
    total: number;
    por_tipo: Record<string, number>;
    ativos: number;
    inativos: number;
    novos_mes: number;
    ultimo_acesso: Array<{
      usuario: Usuario;
      ultimo_acesso: string;
    }>;
  }>> {
    return ApiService.get(`${this.BASE_URL}/estatisticas`);
  }

  /**
   * Obter médicos ativos
   */
  static async obterMedicos(): Promise<ApiResponse<Usuario[]>> {
    return ApiService.get<Usuario[]>(`${this.BASE_URL}?tipo=medico&ativo=true&limit=1000`);
  }

  /**
   * Obter enfermeiros ativos
   */
  static async obterEnfermeiros(): Promise<ApiResponse<Usuario[]>> {
    return ApiService.get<Usuario[]>(`${this.BASE_URL}?tipo=enfermeiro&ativo=true&limit=1000`);
  }

  /**
   * Obter pacientes ativos
   */
  static async obterPacientes(): Promise<ApiResponse<Usuario[]>> {
    return ApiService.get<Usuario[]>(`${this.BASE_URL}?tipo=paciente&ativo=true&limit=1000`);
  }

  /**
   * Buscar usuários por nome ou email
   */
  static async buscar(termo: string, tipo?: string): Promise<ApiResponse<Usuario[]>> {
    const params: any = { busca: termo, limit: 50 };
    if (tipo) params.tipo = tipo;
    
    const queryString = buildQueryString(params);
    return ApiService.get<Usuario[]>(`${this.BASE_URL}?${queryString}`);
  }

  /**
   * Exportar lista de usuários
   */
  static async exportar(filtros: FiltroUsuarios = {}, formato: 'csv' | 'xlsx' = 'xlsx'): Promise<void> {
    const queryString = buildQueryString({ ...filtros, formato });
    const url = `${this.BASE_URL}/exportar?${queryString}`;
    
    await ApiService.download(url, `usuarios.${formato}`);
  }

  /**
   * Importar usuários de arquivo
   */
  static async importar(arquivo: File): Promise<ApiResponse<{
    importados: number;
    erros: Array<{ linha: number; erro: string }>;
  }>> {
    return ApiService.upload(`${this.BASE_URL}/importar`, arquivo);
  }

  /**
   * Obter template para importação
   */
  static async obterTemplateImportacao(): Promise<void> {
    await ApiService.download(`${this.BASE_URL}/template-importacao`, 'template_usuarios.xlsx');
  }

  /**
   * Validar dados de usuário
   */
  static async validarDados(dados: Partial<CriarUsuarioData>): Promise<ApiResponse<{
    valido: boolean;
    erros: Record<string, string>;
  }>> {
    return ApiService.post(`${this.BASE_URL}/validar`, dados);
  }

  /**
   * Obter histórico de atividades do usuário
   */
  static async obterHistoricoAtividades(id: string, limite: number = 50): Promise<ApiResponse<Array<{
    id: number;
    acao: string;
    descricao: string;
    data: string;
    ip_address: string;
  }>>> {
    return ApiService.get(`${this.BASE_URL}/${id}/historico?limite=${limite}`);
  }

  /**
   * Bloquear usuário temporariamente
   */
  static async bloquear(id: string, motivo: string, duracao?: number): Promise<ApiResponse<void>> {
    return ApiService.post(`${this.BASE_URL}/${id}/bloquear`, {
      motivo,
      duracao, // em horas
    });
  }

  /**
   * Desbloquear usuário
   */
  static async desbloquear(id: string): Promise<ApiResponse<void>> {
    return ApiService.post(`${this.BASE_URL}/${id}/desbloquear`);
  }

  /**
   * Enviar notificação para usuário
   */
  static async enviarNotificacao(id: string, titulo: string, mensagem: string): Promise<ApiResponse<void>> {
    return ApiService.post(`${this.BASE_URL}/${id}/notificacao`, {
      titulo,
      mensagem,
    });
  }

  /**
   * Obter permissões do usuário
   */
  static async obterPermissoes(id: string): Promise<ApiResponse<{
    permissoes: string[];
    grupos: string[];
  }>> {
    return ApiService.get(`${this.BASE_URL}/${id}/permissoes`);
  }

  /**
   * Atualizar permissões do usuário
   */
  static async atualizarPermissoes(id: string, permissoes: string[]): Promise<ApiResponse<void>> {
    return ApiService.put(`${this.BASE_URL}/${id}/permissoes`, { permissoes });
  }

  /**
   * Atualizar perfil do usuário logado
   */
  static async atualizarPerfil(dados: Partial<Usuario>): Promise<ApiResponse<UpdateProfileResponse>> {
    return ApiService.put<UpdateProfileResponse>('/usuarios/perfil', dados);
  }

  /**
   * Upload de avatar do usuário
   */
  static async uploadAvatar(file: File): Promise<ApiResponse<{ avatar: string }>> {
    const formData = new FormData();
    formData.append('avatar', file);
    
    return ApiService.post<{ avatar: string }>('/upload/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }
}

export default UsuariosService;